import { Box, Button, styled, Typography } from '@mui/material';
import { ModuleT } from 'business/constants';
import _ from 'lodash';
import React, { useState, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';
import CustomizationForm from './CustomizationForm/CustomizationForm';
import { useUpdateClientPaletteMutation } from './hooks/updateClientPalette';
import { useAddIntegrationMutation } from './hooks/updateIntegrations';
import { useUpdateClientModulesMutation } from './hooks/updateModules';
import IntegrationForm from './IntegrationForm/IntegrationForm';
import {
    DecisionPerimeterT,
    ExtraFeatureT,
} from './ModuleForm/helpers/definitions';
import { ClientPalette } from './ModuleForm/helpers/types';
import ModuleForm from './ModuleForm/ModuleForm';
import { useUpdateUsageHistoryMutation } from './hooks/updateUsageHistory';
import UsageHistoryForm from './UsageHistoryForm/UsageHistoryForm';
import DecisionPerimeters from './DecisionPerimeter';
import ApiKeyForm from './ApiKeyForm/ApiKeyForm';

const StyItem = styled(Box, { name: 'StyItem' })(({ theme }) => ({
    padding: theme.spacing(2),
}));

const isNew = <T extends ModuleT | ExtraFeatureT | DecisionPerimeterT>(
    prev: T[],
    next: T[],
) => !prev.every((item) => next.includes(item)) || prev.length !== next.length;

const ClientConfiguration = ({ client }: { client: ClientT }) => {
    const { t } = useTranslation();
    const initialPalette = {
        primaryColor: client.primaryColor || undefined,
        secondaryColor: client.secondaryColor || undefined,
        logoUrl: client.logoUrl,
    };
    const initialUsageHistory = client.hasUsageHistory || false;
    const [clientPalette, setClientPalette] =
        useState<ClientPalette>(initialPalette);
    const [newSoftwares, setNewSoftwares] = useState<string[]>([]);
    const [modules, setModules] = useState<ModuleT[]>(_.sortBy(client.modules));
    const [extraFeatures, setExtraFeatures] = useState<ExtraFeatureT[]>(
        client.extraFeatures,
    );
    const [decisionPerimeters, setDecisionPerimeters] = useState<
        DecisionPerimeterT[]
    >(client.decisionPerimeters);

    const [usageHistory, setUsageHistory] =
        useState<boolean>(initialUsageHistory);
    const [shouldSaveModules, setShouldSaveModules] = useState<boolean>(false);

    useEffect(() => {
        setShouldSaveModules(
            isNew(client.modules, modules) ||
                isNew(client.extraFeatures, extraFeatures) ||
                isNew(client.decisionPerimeters, decisionPerimeters),
        );
    }, [
        JSON.stringify(modules),
        JSON.stringify(extraFeatures),
        JSON.stringify(decisionPerimeters),
    ]);

    const { bind } = client;
    const clientPaletteMutation = useUpdateClientPaletteMutation(bind);
    const usageHistoryMutation = useUpdateUsageHistoryMutation(bind);
    const addIntegrationMutation = useAddIntegrationMutation(
        bind,
        setNewSoftwares,
    );
    const updateClientModulesMutation = useUpdateClientModulesMutation(bind);

    const onSave = () => {
        if (!_.isEqualWith(clientPalette, initialPalette)) {
            clientPaletteMutation.mutate(clientPalette);
        }
        if (newSoftwares.length > 0) {
            addIntegrationMutation.mutate(newSoftwares);
        }
        if (shouldSaveModules) {
            updateClientModulesMutation.mutate({
                modules,
                extraFeatures,
                decisionPerimeters,
            });
        }
        if (usageHistory !== initialUsageHistory) {
            usageHistoryMutation.mutate(usageHistory);
        }
    };

    return (
        <Flex gap={2} margin={2} direction="column" justify="space-between">
            <StyItem>
                <Typography variant="h4">
                    {t('support.clientConfiguration.customization.title')}
                </Typography>
                <CustomizationForm
                    palette={clientPalette}
                    setPalette={setClientPalette}
                />
            </StyItem>
            <StyItem>
                <Typography variant="h4">
                    {t('support.clientConfiguration.modules')}
                </Typography>
                <Flex margin="10px" direction="column" maxWidth="50%">
                    <ModuleForm
                        modules={modules}
                        setModules={setModules}
                        extraFeatures={extraFeatures}
                        setExtraFeatures={setExtraFeatures}
                    />
                </Flex>
                <DecisionPerimeters
                    decisionPerimeters={decisionPerimeters}
                    setDecisionPerimeters={setDecisionPerimeters}
                />
            </StyItem>
            <StyItem>
                <Typography variant="h4">
                    {t('support.clientConfiguration.integrations.title')}
                </Typography>
                <IntegrationForm
                    isNew={false}
                    bind={client.bind}
                    newSoftwares={newSoftwares}
                    setNewSoftwares={setNewSoftwares}
                />
            </StyItem>
            <StyItem>
                <Typography variant="h4">
                    {t('support.clientConfiguration.usageHistory')}
                </Typography>
                <UsageHistoryForm
                    usageHistory={usageHistory}
                    setUsageHistory={setUsageHistory}
                />
            </StyItem>
            <StyItem>
                <Typography variant="h4">
                    {t('support.clientConfiguration.apiKey.title')}
                </Typography>
                <ApiKeyForm bind={bind} />
            </StyItem>
            <StyItem>
                <Button
                    sx={{ m: 1, height: '1rem', width: '300px' }}
                    variant="contained"
                    color="primary"
                    onClick={onSave}
                >
                    {t('common.save')}
                </Button>
            </StyItem>
        </Flex>
    );
};

export default memo(ClientConfiguration, (prevProps, nextProps) =>
    _.isEqualWith(prevProps.client, nextProps.client),
);
